import type { RosterColumn } from '../../services/roster/roster.columns';
import type { RosterRecord } from '../../services/roster/roster.types';
import { RosterStore } from '../../stores/roster/roster.store';
import { BaseButton } from '../base/button/button';
import { BaseInput } from '../base/input/input';
import { DataTable, ITableFetchOptions } from '../data-table/data-table';
import { DataTablePresenter } from '../data-table/data-table.presenter';
import { reaction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import { Row, TableInstance } from 'react-table';
import { RosterBulkBar } from './roster-bulk-bar';
import * as S from './roster-table.styles';

interface IRosterTableProps {
    store: RosterStore;
    presenter: DataTablePresenter<RosterRecord>;
    getVisibleColumns: () => RosterColumn[];
}

const PAGE_SIZES = [20, 50, 100];

/**
 * The Roster table: the core `DataTable`, so paging, sorting, row selection and the column picker
 * are the same controls as on every other support screen (NS-1). Search, the record count and
 * "Clear filters" sit at the start of its controls row, where the design puts them; the selection
 * bar sits between the controls and the rows.
 */
export const RosterTable = observer(
    ({ store, presenter, getVisibleColumns }: IRosterTableProps): React.ReactElement => {
        const table = useRef<TableInstance<RosterRecord> | undefined>(undefined);

        // A Disable can empty the last page. The table owns its page index, so move it back here.
        useEffect(
            () =>
                reaction(
                    () => store.outOfRangePage,
                    page => {
                        if (page !== undefined) {
                            (table.current as unknown as { gotoPage?: (index: number) => void })?.gotoPage?.(page);
                        }
                    },
                ),
            [store],
        );

        const handleFetch = (options: ITableFetchOptions): void => {
            const [sort] = options.sortBy;
            void store.loadPage({
                pageIndex: options.pageIndex,
                pageSize: options.pageSize,
                sort: sort ? { key: sort.id, direction: sort.desc ? 'desc' : 'asc' } : undefined,
            });
        };

        return (
            <DataTable
                presenter={presenter}
                isLoading={store.loading}
                hideFiltering={true}
                selectableRows={true}
                pageSizeOptions={PAGE_SIZES}
                onFetch={handleFetch}
                onRowClick={(row: Row<RosterRecord>): void => void store.openRecord(row.original.id)}
                renderControlsLeading={(): React.ReactElement => <RosterSearch store={store} />}
                renderToolbar={(): React.ReactElement => (
                    <RosterBulkBar store={store} presenter={presenter} getVisibleColumns={getVisibleColumns} />
                )}
                onTableReady={(instance: TableInstance<RosterRecord>): void => {
                    table.current = instance;
                }}
                NotFoundComponent={(): React.ReactElement => <RosterEmpty store={store} />}
            />
        );
    },
);

const RosterSearch = observer(({ store }: { store: RosterStore }): React.ReactElement => {
    const placeholder =
        store.tab === 'providers' ? 'Search providers, NPI, organization…' : 'Search facilities, NPI, address…';

    return (
        <S.Leading>
            <S.Search>
                <BaseInput
                    type="search"
                    value={store.search}
                    placeholder={placeholder}
                    aria-label={placeholder}
                    disableLatin1Validation={true}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => store.setSearch(e.target.value)}
                    onDelete={store.search ? (): void => store.setSearch('') : undefined}
                />
            </S.Search>
            <S.RecordCount aria-live="polite">{recordLabel(store)}</S.RecordCount>
            {store.hasFilters && (
                <BaseButton variant="text" size="small" onClick={store.clearFilters}>
                    Clear filters
                </BaseButton>
            )}
        </S.Leading>
    );
});

/**
 * What an empty table says. The core table shows this whenever it has no rows and is not loading —
 * which includes after a failed request, so the failure case must be said here too: "no results"
 * over a list that never loaded is the confident wrong answer (NS-3).
 */
const RosterEmpty = observer(({ store }: { store: RosterStore }): React.ReactElement => {
    const noun = store.tab === 'providers' ? 'providers' : 'facilities';
    if (store.error) {
        return (
            <S.Empty>
                <S.EmptyTitle>Couldn't load {noun}.</S.EmptyTitle>
                <S.EmptyHint>This list is unknown, not empty. Retry from the banner above.</S.EmptyHint>
            </S.Empty>
        );
    }
    if (store.hasFilters) {
        return (
            <S.Empty>
                <S.EmptyTitle>No results.</S.EmptyTitle>
                <S.EmptyHint>
                    {store.tab === 'providers' ? 'Adjust the search or type filter.' : 'Adjust the search.'}
                </S.EmptyHint>
            </S.Empty>
        );
    }
    return (
        <S.Empty>
            <S.EmptyTitle>No {noun} on file.</S.EmptyTitle>
        </S.Empty>
    );
});

/** "Loading…" and "Records unavailable" are not counts; only a loaded page gets a number (NS-3). */
function recordLabel(store: RosterStore): string {
    if (store.error) {
        return 'Records unavailable';
    }
    if (store.total === undefined) {
        return 'Loading…';
    }
    // An incomplete search's count is a floor, not a total.
    if (store.page?.truncated) {
        return `${store.total}+ records`;
    }
    return `${store.total} ${store.total === 1 ? 'record' : 'records'}`;
}
